import { faChartMixed } from "@fortawesome/pro-regular-svg-icons";

import { AnalyticsCopilotSidebar } from "src/analytics/copilot/AnalyticsCopilotSidebar";
import type { ChartExploreContext } from "src/analytics/copilot/chartExploreContext";
import type { DashboardPinning } from "src/analytics/copilot/LiveAnalyticsMessages";
import { MAX_PINS, useDashboard } from "src/analytics/dashboard/useDashboard";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { useWorkspaceInsightsChat } from "src/analytics/workspace/useWorkspaceInsightsChat";
import { useListDashboardsQuery } from "src/api/analytics-dashboard/dashboard";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { Realm } from "src/clients/analytics-dashboard";

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "How is my decision volume progressing over time?",
    prompt: "How is my decision volume progressing over time?",
  },
  {
    label: "Which decision flows processed the most decisions last month?",
    prompt: "Which decision flows processed the most decisions last month?",
  },
  {
    label: "Show me decision outcomes split by flow for the last 30 days",
    prompt: "Show me decision outcomes split by flow for the last 30 days",
  },
];

export const WorkspaceCopilotSidebar: React.FC<{
  onClose: () => void;
  wsId: string;
  labelDictionaries: LabelDictionaries;
  dashboardId?: string;
  pendingStructuredInput?: ChartExploreContext | null;
  onClearPendingStructuredInput?: () => void;
}> = ({
  onClose,
  wsId,
  labelDictionaries,
  dashboardId,
  pendingStructuredInput,
  onClearPendingStructuredInput,
}) => {
  const chat = useWorkspaceInsightsChat({ wsId });
  const { data: dashboards } = useListDashboardsQuery(Realm.WORKSPACE, wsId);
  const dashboardName = dashboards?.find((d) => d.id === dashboardId)?.name;
  const {
    dashboard,
    pinChart,
    unpinChart,
    isAtPinCap,
    isLoading,
    hasLoadFailed,
  } = useDashboard(Realm.WORKSPACE, wsId, dashboardId, {
    enabled: dashboardId !== undefined,
  });

  const pinning: DashboardPinning = {
    dashboard,
    pinChart,
    unpinChart,
    pinDisabled: isLoading || hasLoadFailed || isAtPinCap,
    pinDisabledReason: hasLoadFailed
      ? "The dashboard failed to load, reload the page to pin charts"
      : isAtPinCap
        ? `Dashboards hold up to ${MAX_PINS} charts, remove one to pin a new chart`
        : undefined,
  };

  return (
    <AnalyticsCopilotSidebar
      chat={chat}
      contextPill={
        dashboardName
          ? {
              icon: faChartMixed,
              label: dashboardName,
              key: "dashboard-context",
            }
          : undefined
      }
      dataLoc="workspace-insights-copilot-sidebar"
      dataSource="flow_analytics"
      emptyStateDataLoc="workspace-insights-copilot-empty-state"
      labelDictionaries={labelDictionaries}
      onClearPendingStructuredInput={onClearPendingStructuredInput}
      onClose={onClose}
      pendingStructuredInput={pendingStructuredInput}
      pinning={pinning}
      storageKey="workspace-insights-copilot-sidebar-width"
      submitLabel={chat.messages.length > 0 ? "Send" : "Start Chat"}
      suggestionPrompts={SUGGESTION_PROMPTS}
    />
  );
};
